import React, { useState, useEffect } from 'react';
import { Button, Form, FormControl, Alert, Row, Col } from 'react-bootstrap';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { fetchUnitsForTenant } from '../../services/unitsService';
import { createQuestion, updateQuestionContent } from '../../services/questionsService';
import QnAQuestion from '../../models/QnAQuestion';
import { QNA_STATUS } from '../../platform/qnaConstants';
import { GENERAL_UNIT_ID, isGeneralUnitId } from '../../platform/constants';
import { canEditQuestionContent } from '../../platform/qnaPermissions';
import './QnACreateQuestion.css';
import { useToast } from '../../platform/toast/ToastProvider';

function QnACreateQuestion({ onQuestionCreated, question, onQuestionUpdated }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [unit, setUnit] = useState(GENERAL_UNIT_ID);
  const [unitName, setUnitName] = useState('Splošno');
  const [units, setUnits] = useState([]);
  const { user } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();
  const toast = useToast();

  const isEditing = !!question;

  useEffect(() => {
    if (!user || !tenantId) return;
    fetchUnitsForTenant(tenantId, { user, memberships }).then(setUnits);
  }, [user, tenantId, memberships]);

  useEffect(() => {
    if (isEditing && question) {
      setTitle(question.title);
      setBody(question.body || question.title || '');
      setUnit(question.unit);
      setUnitName(question.unitName);
    }
  }, [isEditing, question]);

  const handleUnitChange = (event) => {
    const selectedUnitId = event.target.value;
    const selectedUnit = units.find((item) => item.id === selectedUnitId);
    setUnit(selectedUnitId);
    setUnitName(selectedUnit ? selectedUnit.unitName : 'Splošno');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!user) {
      setError('Za objavo vprašanja morate biti prijavljeni.');
      return;
    }
    if (!title.trim()) {
      setError('Zadeva ne sme biti prazna.');
      return;
    }
    if (!body.trim()) {
      setError('Vprašanje ne sme biti prazno.');
      return;
    }

    if (isEditing && !canEditQuestionContent(user, question)) {
      setError('Urejanje je mogoče le pri odprtih vprašanjih.');
      return;
    }

    try {
      if (isEditing) {
        await updateQuestionContent(question.id, {
          title: title.trim(),
          body: body.trim(),
          unit,
          unitName,
        });
        setError('');
        toast.success('Vprašanje je bilo posodobljeno.');
        if (onQuestionUpdated) onQuestionUpdated();
      } else {
        const newQuestion = new QnAQuestion({
          title: title.trim(),
          body: body.trim(),
          status: QNA_STATUS.OPEN,
          uid: user.uid,
          displayName: user.displayName,
          unit,
          unitName,
        });

        await createQuestion(tenantId, newQuestion);
        setTitle('');
        setBody('');
        setUnit(GENERAL_UNIT_ID);
        setUnitName('Splošno');
        setError('');
        toast.success('Vprašanje je bilo objavljeno.');
        onQuestionCreated();
      }
    } catch (err) {
      setError('Shranjevanje vprašanja ni uspelo. Poskusite znova.');
      toast.error('Shranjevanje vprašanja ni uspelo. Poskusite znova.');
      console.error('Error saving question:', err);
    }
  };

  return (
    <div className="platform-form-card">
      <div className="platform-form-card__body">
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <Form.Label>Zadeva</Form.Label>
              <FormControl
                type="text"
                placeholder="Kratek naslov vprašanja"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <Form.Label>Vprašanje</Form.Label>
              <FormControl
                as="textarea"
                rows={6}
                placeholder="Opišite vprašanje podrobneje"
                value={body}
                onChange={(event) => setBody(event.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <Form.Label>Enota</Form.Label>
              <Form.Control
                as="select"
                value={unit}
                onChange={handleUnitChange}
                disabled={isEditing}
              >
                <option value={GENERAL_UNIT_ID}>Splošno</option>
                {units
                  .filter((item) => !isGeneralUnitId(item.id, tenantId))
                  .map((item) => (
                    <option key={item.id} value={item.id}>{item.unitName}</option>
                  ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        <Button variant="primary" type="submit" className="w-100">
          {isEditing ? 'Posodobi' : 'Pošlji vprašanje'}
        </Button>
      </Form>
      </div>
    </div>
  );
}

export default QnACreateQuestion;
